<template>
  <div id="app" style="width: 100%">
    <div class="page-header">
      <div class="page-title">报修管理</div>
      <div class="page-header-btn">
        搜索:
        <el-input v-model="searchKeyword" placeholder="关键字" size="mini" style="margin-right: 1rem; width: auto;"
          prefix-icon="el-icon-search" @input="searchRepairRequests" clearable></el-input>
        状态:
        <el-select v-model="statusFilter" placeholder="状态筛选" size="mini" style="width: 8rem; margin-right: 1rem;"
          @change="filterByStatus" clearable>
          <el-option label="全部" value=""></el-option>
          <el-option label="待处理" value="pending"></el-option>
          <el-option label="处理中" value="processing"></el-option>
          <el-option label="已完成" value="completed"></el-option>
        </el-select>
        <el-button icon="el-icon-refresh-right" circle size="mini" @click="getRepairRequestList"></el-button>
      </div>
    </div>
    
    <!-- 报修列表 -->
    <el-table 
      size="medium" 
      :data="repairRequestList" 
      style="width: 100%; font-size: 12px;" 
      :height="$tableHeght"
      header-row-class-name="table-header"
    >
      <el-table-column prop="id" label="ID" width="80"/>
      <el-table-column prop="title" label="报修标题" min-width="200" show-overflow-tooltip/>
      <el-table-column prop="description" label="问题描述" min-width="250" show-overflow-tooltip/>
      <el-table-column prop="username" label="用户" min-width="120">
        <template v-slot:default="scope">
          {{ scope.row.username || '微信用户' }}
        </template>
      </el-table-column>
      <el-table-column prop="phone" label="用户电话" min-width="120" show-overflow-tooltip>
        <template v-slot:default="scope">
          {{ scope.row.phone || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="address" label="地址" min-width="200" show-overflow-tooltip>
        <template v-slot:default="scope">
          {{ scope.row.address || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template v-slot:default="scope">
          <el-tag 
            :type="getStatusType(scope.row)" 
            size="mini"
          >
            {{ getStatusText(scope.row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="提交时间" width="160">
        <template v-slot:default="scope">
          {{ formatTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="updateTime" label="更新时间" width="160">
        <template v-slot:default="scope">
          {{ formatTime(scope.row.updateTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template v-slot:default="scope">
          <el-button size="mini" icon="el-icon-view" type="text" @click="viewDetail(scope.row)">查看</el-button>
          <el-divider direction="vertical"></el-divider>
          <el-button 
            size="mini" 
            icon="el-icon-edit" 
            type="text" 
            @click="processRepair(scope.row)"
            :disabled="scope.row.completed"
          >
            处理
          </el-button>
          <el-divider direction="vertical"></el-divider>
          <el-button 
            size="mini" 
            icon="el-icon-delete" 
            type="text" 
            @click="deleteRepair(scope.row)"
            style="color: #f56c6c"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <el-pagination
      style="text-align: right; margin-top: 20px;"
      @size-change="handleSizeChange"
      @current-change="currentChange"
      :current-page="currentPage"
      :page-size="count"
      :page-sizes="[15, 25, 35, 50]"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
    >
    </el-pagination>
    
    <!-- 详情对话框 -->
    <el-dialog title="报修详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="selectedRepair">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="报修ID">{{ selectedRepair.id }}</el-descriptions-item>
          <el-descriptions-item label="标题">{{ selectedRepair.title }}</el-descriptions-item>
          <el-descriptions-item label="问题描述">
            <div style="white-space: pre-wrap;">{{ selectedRepair.description }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="用户">{{ selectedRepair.username || '微信用户' }}</el-descriptions-item>
          <el-descriptions-item label="用户电话">{{ selectedRepair.phone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="地址">{{ selectedRepair.address || '-' }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusType(selectedRepair)" size="mini">
              {{ getStatusText(selectedRepair) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ formatTime(selectedRepair.createTime) }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ formatTime(selectedRepair.updateTime) }}</el-descriptions-item>
          <el-descriptions-item label="处理结果" v-if="selectedRepair.result">
            <div style="white-space: pre-wrap;">{{ selectedRepair.result }}</div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button 
          type="primary" 
          @click="processRepair(selectedRepair)"
          :disabled="selectedRepair && selectedRepair.completed"
        >
          处理报修
        </el-button>
      </div>
    </el-dialog>
    
    <!-- 处理对话框 -->
    <el-dialog title="处理报修" :visible.sync="processDialogVisible" width="500px">
      <el-form :model="processForm" label-width="80px">
        <el-form-item label="状态">
          <el-select v-model="processForm.completed" placeholder="请选择状态">
            <el-option label="处理中" :value="false"></el-option>
            <el-option label="已完成" :value="true"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="处理结果">
          <el-input 
            v-model="processForm.result" 
            type="textarea" 
            :rows="4" 
            placeholder="请输入处理结果或备注"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="processDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitProcess" :loading="processing">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'RepairRequestManager',
  data() {
    return {
      repairRequestList: [],
      currentPage: 1,
      count: 15,
      total: 0,
      searchKeyword: '',
      statusFilter: '',
      detailDialogVisible: false,
      processDialogVisible: false,
      selectedRepair: null,
      processForm: {
        completed: false,
        result: ''
      },
      processing: false
    }
  },
  mounted() {
    this.getRepairRequestList()
  },
  methods: {
    getRepairRequestList() {
      let params = {
        page: this.currentPage,
        count: this.count
      }
      
      if (this.searchKeyword) {
        params.keyword = this.searchKeyword
      }
      
      if (this.statusFilter) {
        params.status = this.statusFilter
      }
      
      this.$axios({
        method: 'get',
        url: '/api/repair/list',
        params: params
      }).then((res) => {
        if (res.data.code === 0) {
          this.repairRequestList = res.data.data.list || []
          this.total = res.data.data.total || 0
        } else {
          this.$message.error(res.data.msg || '获取报修列表失败')
        }
      }).catch((error) => {
        console.error('获取报修列表失败:', error)
        this.$message.error('获取报修列表失败')
      })
    },
    
    searchRepairRequests() {
      this.currentPage = 1
      this.getRepairRequestList()
    },
    
    filterByStatus() {
      this.currentPage = 1
      this.getRepairRequestList()
    },
    
    handleSizeChange(val) {
      this.count = val
      this.getRepairRequestList()
    },
    
    currentChange(val) {
      this.currentPage = val
      this.getRepairRequestList()
    },
    
    viewDetail(row) {
      this.selectedRepair = row
      this.detailDialogVisible = true
    },
    
    processRepair(row) {
      this.selectedRepair = row
      this.processForm = {
        completed: row.completed || false,
        result: row.result || ''
      }
      this.processDialogVisible = true
      this.detailDialogVisible = false
    },
    
    submitProcess() {
      if (this.processForm.completed === null || this.processForm.completed === undefined) {
        this.$message.warning('请选择状态')
        return
      }
      
      this.processing = true
      
      this.$axios({
        method: 'post',
        url: '/api/repair/process',
        params: {
          id: this.selectedRepair.id,
          processResult: this.processForm.result,
          completed: this.processForm.completed
        }
      }).then((res) => {
        if (res.data.code === 0) {
          this.$message.success('处理成功')
          this.processDialogVisible = false
          this.getRepairRequestList()
        } else {
          this.$message.error(res.data.msg || '处理失败')
        }
      }).catch((error) => {
        console.error('处理失败:', error)
        this.$message.error('处理失败')
      }).finally(() => {
        this.processing = false
      })
    },
    
    deleteRepair(row) {
      this.$confirm('确定要删除这条报修记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$axios({
          method: 'delete',
          url: `/api/repair/delete/${row.id}`
        }).then((res) => {
          if (res.data.code === 0) {
            this.$message.success('删除成功')
            this.getRepairRequestList()
          } else {
            this.$message.error(res.data.msg || '删除失败')
          }
        }).catch((error) => {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        })
      })
    },
    
    getStatusType(row) {
      if (row.completed) {
        return 'success'
      } else if (row.processed) {
        return 'primary'
      } else {
        return 'warning'
      }
    },
    
    getStatusText(row) {
      if (row.completed) {
        return '已完成'
      } else if (row.processed) {
        return '处理中'
      } else {
        return '待处理'
      }
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}

.el-descriptions {
  margin-top: 20px;
}

.el-tag {
  margin-left: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/RepairRequestManager.vue